// Notification Service for Push Notifications

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

class NotificationService {
  constructor() {
    this.registration = null;
    this.subscription = null;
  }

  // Check if browser supports notifications
  isSupported() {
    return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  }

  // Get current notification permission
  getPermission() {
    if (!this.isSupported()) return 'denied';
    return Notification.permission;
  }

  // Request notification permission
  async requestPermission() {
    if (!this.isSupported()) {
      throw new Error('Push notifications are not supported in this browser');
    }

    const permission = await Notification.requestPermission();
    return permission;
  }

  // Register service worker
  async registerServiceWorker() {
    if (!this.isSupported()) {
      throw new Error('Service Worker is not supported');
    }

    try {
      this.registration = await navigator.serviceWorker.register('/service-worker.js');
      console.log('Service Worker registered:', this.registration);
      
      // Wait for service worker to be ready
      await navigator.serviceWorker.ready;
      
      return this.registration;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      throw error;
    }
  }

  // Subscribe to push notifications
  async subscribe(token) {
    if (!this.registration) {
      await this.registerServiceWorker();
    }

    try {
      // Check if already subscribed
      const existingSubscription = await this.registration.pushManager.getSubscription();
      if (existingSubscription) {
        this.subscription = existingSubscription;
        console.log('Already subscribed to push notifications');
        return existingSubscription;
      }

      // Create new subscription
      // Note: In production, you would use your VAPID public key here
      const subscription = await this.registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(
          // This is a dummy key - in production, replace with your VAPID public key
          'BEl62iUYgUivxIkv69yViEuiBIa-Ib9-SkvMeAtA3LFgDzkrxZJjSgSnfckjBJuBkr3qBUYIHBQFLXYp5Nksh8U'
        )
      });

      this.subscription = subscription;
      console.log('Push subscription:', subscription);

      // Send subscription to backend
      await this.sendSubscriptionToBackend(subscription, token);

      return subscription;
    } catch (error) {
      console.error('Failed to subscribe to push notifications:', error);
      throw error;
    }
  }

  // Unsubscribe from push notifications
  async unsubscribe(token) {
    if (!this.subscription) {
      const subscription = await this.registration.pushManager.getSubscription();
      if (subscription) {
        this.subscription = subscription;
      }
    }

    if (this.subscription) {
      try {
        await this.subscription.unsubscribe();
        await this.removeSubscriptionFromBackend(token);
        this.subscription = null;
        console.log('Unsubscribed from push notifications');
        return true;
      } catch (error) {
        console.error('Failed to unsubscribe:', error);
        throw error;
      }
    }
    return false;
  }

  // Check if currently subscribed
  async isSubscribed() {
    if (!this.registration) {
      try {
        this.registration = await navigator.serviceWorker.ready;
      } catch (error) {
        return false;
      }
    }

    const subscription = await this.registration.pushManager.getSubscription();
    return subscription !== null;
  }

  // Send subscription to backend
  async sendSubscriptionToBackend(subscription, token) {
    try {
      const response = await fetch(`${API}/notifications/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          subscription: subscription.toJSON()
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save subscription on server');
      }

      return await response.json();
    } catch (error) {
      console.error('Error sending subscription to backend:', error);
      throw error;
    }
  }

  // Remove subscription from backend
  async removeSubscriptionFromBackend(token) {
    try {
      await fetch(`${API}/notifications/unsubscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
    } catch (error) {
      console.error('Error removing subscription from backend:', error);
    }
  }

  // Show a local notification (for testing)
  async showLocalNotification(title, options = {}) {
    if (!this.isSupported()) {
      throw new Error('Notifications are not supported');
    }

    if (Notification.permission !== 'granted') {
      throw new Error('Notification permission not granted');
    }

    if (!this.registration) {
      await this.registerServiceWorker();
    }

    return this.registration.showNotification(title, {
      body: options.body || '',
      icon: options.icon || '/logo.png',
      badge: '/logo.png',
      vibrate: [200, 100, 200],
      tag: options.tag || 'notification',
      ...options
    });
  }

  // Helper function to convert VAPID key
  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/\-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }
}

export default new NotificationService();
